import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Toggle }   from '../components/shared/Toggle';
import { Spinner }  from '../components/shared/Spinner';
import { settingsAPI } from '../services/api';
import { useAuth }     from '../contexts/AuthContext';
import { ROLE_LABELS } from '../components/shared/fmt';
import { useTitle }    from '../hooks/useTitle';

const SettingRow: React.FC<{ label:string; value:string; editable?:boolean; onEdit?:()=>void }> = ({ label, value, editable, onEdit }) => (
  <div className="setting-row">
    <span style={{ fontSize:13, fontWeight:600 }}>{label}</span>
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <span style={{ fontSize:12, color:'var(--muted)' }}>{value}</span>
      {editable && <button className="btn-secondary" style={{ fontSize:10, padding:'3px 8px' }} onClick={onEdit}>✏️</button>}
    </div>
  </div>
);

export const SettingsPage: React.FC = () => {
  useTitle('Settings');
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey:['settings'], queryFn: settingsAPI.get });
  const settings = data?.data || {};

  const [togs, setTogs] = useState({
    autoReceipt: true, kdsEnabled: true, loyaltyEnabled: true, deliveryEnabled: false,
    sound: false, darkMode: true, notifications: true, autoBackup: true,
  });

  const updateMut = useMutation({ mutationFn: settingsAPI.update, onSuccess:()=>{ toast.success('Settings saved'); qc.invalidateQueries({queryKey:['settings']}); } });

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="page-title">System Settings</div>
      <div className="page-sub">Configure your Teknuluguy POS system</div>

      <div className="grid-2">
        {/* Restaurant info */}
        <div className="card">
          <div className="card-title" style={{ color:'var(--accent)' }}>🏪 Restaurant Information</div>
          <SettingRow label="Name"        value={settings.restaurantName||'Teknuluguy'} editable onEdit={()=>toast('Connect to backend')} />
          <SettingRow label="Branch"      value={settings.branch||'Main Branch'} editable />
          <SettingRow label="Address"     value={settings.address||'—'} />
          <SettingRow label="Phone"       value={settings.phone||'—'} editable />
          <SettingRow label="Email"       value={settings.email||'—'} editable />
          <SettingRow label="CR Number"   value={settings.crNumber||'—'} />
          <SettingRow label="VAT Number"  value={settings.vatNumber||'—'} />
          <SettingRow label="Tax Rate"    value={`${((settings.taxRate||0.15)*100).toFixed(0)}%`} />
          <SettingRow label="Currency"    value={settings.currency||'KWD'} />
        </div>

        {/* POS preferences */}
        <div>
          <div className="card">
            <div className="card-title" style={{ color:'var(--accent)' }}>⚙️ POS Preferences</div>
            {([
              ['Auto-print Receipt',  'autoReceipt'],
              ['Kitchen Display',     'kdsEnabled'],
              ['Loyalty Program',     'loyaltyEnabled'],
              ['Delivery Orders',     'deliveryEnabled'],
              ['Sound Notifications', 'sound'],
              ['Dark Mode',           'darkMode'],
              ['Notifications',       'notifications'],
              ['Auto Backup (Daily)', 'autoBackup'],
            ] as [string, keyof typeof togs][]).map(([l,k]) => (
              <div key={k} className="setting-row">
                <span style={{ fontSize:13, fontWeight:600 }}>{l}</span>
                <Toggle on={togs[k]} onChange={()=>setTogs(t=>({...t,[k]:!t[k]}))} />
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-title" style={{ color:'var(--accent)' }}>🔒 Security</div>
            <SettingRow label="Your Role"         value={ROLE_LABELS[user?.role||'']||'—'} />
            <SettingRow label="Logged in as"      value={user?.name||'—'} />
            <SettingRow label="Session Timeout"   value="12 hours" />
            <SettingRow label="PIN Complexity"    value="4 digits required" />
            <SettingRow label="Void Requires"     value="Manager approval" />
            <button className="btn-primary" style={{ width:'100%', marginTop:14 }}>🔑 Change Password</button>
          </div>
        </div>
      </div>

      {/* Receipt settings */}
      <div className="card">
        <div className="card-title" style={{ color:'var(--accent)' }}>🧾 Receipt Configuration</div>
        <div className="grid-2">
          <div>
            <SettingRow label="Receipt Header"  value={settings.receiptHeader||'TEKNULUGUY'} editable />
            <SettingRow label="Footer (Arabic)" value={settings.receiptFooter||'شكراً لزيارتكم'} editable />
            <SettingRow label="Show Logo"       value="Yes" />
            <SettingRow label="Print Copies"    value="1" />
          </div>
          <div>
            <SettingRow label="Language"           value="English + Arabic" />
            <SettingRow label="Show Itemized Tax"  value="Yes" />
            <SettingRow label="Show Deal Code"     value="Yes" />
            <SettingRow label="Print on Payment"   value={togs.autoReceipt?'Automatic':'Manual'} />
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ border:'1px solid rgba(239,68,68,.3)' }}>
        <div className="card-title" style={{ color:'var(--red)' }}>⚠️ Danger Zone</div>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          {[
            ["Clear Today's Data",   "Reset today's orders and transactions"],
            ['Reset Menu Prices',    'Revert all prices to defaults'],
            ['Factory Reset',        'Wipe all data — irreversible'],
          ].map(([l,d]) => (
            <div key={l} style={{ background:'rgba(239,68,68,.05)', border:'1px solid rgba(239,68,68,.2)', borderRadius:'var(--rs)', padding:14, flex:'1 1 200px' }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>{l}</div>
              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10 }}>{d}</div>
              <button className="btn-secondary btn-danger" onClick={()=>window.confirm(`Confirm: ${l}`) && toast.error('Action blocked — connect to backend')}>{l}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
